import { QuestionBan } from "types/socket-messages";
import { IQuestionSocket, ISocketCommentProps } from "types/working-model";

export const sortBannedQuestions = (bannedQuestions: QuestionBan[]): [Set<number>, Map<number, number>] => {
	return bannedQuestions.reduce<[Set<number>, Map<number, number>]>((acc, ban) => {
		if (ban.type === 'question') {
			acc[0].add(ban.id);
		} else {
			acc[1].set(ban.question_id, ban.id);
		}
		return acc;
	}, [new Set(), new Map()] as [Set<number>, Map<number, number>]);
};

type QuestionsMapEntry = [number | string, ISocketCommentProps | IQuestionSocket];

// Stable per-card key, matching the react-virtualized row-key scheme in message-list.tsx
// (question_id-id for replies, id for questions). The moderation list windows its rows and a card
// UNMOUNTS when scrolled out of view, so any state that must outlive scrolling (reply draft text,
// reply-open flag) is lifted into the store keyed by this — and the list and the card must agree on
// the key. question/comment ids can collide across the two serial sequences, hence the prefix.
export const getMessageKey = (
	// typed as the same union message-list renders, so callers (and the card's loosely-typed `any`
	// message) assign cleanly without tripping the weak-type / index-signature checks
	message: IQuestionSocket | ISocketCommentProps | null | undefined
): string => (message?.question_id ? `${message.question_id}-${message.id}` : `${message?.id}`);

// `created_at` is epoch-ms from the API in the hot path; skip Date construction when it's already a
// number (this comparator runs O(n log n) times on every list re-sort). Falls back to `new Date()`
// for ISO strings / Date instances, and treats missing/invalid values as epoch 0 (never NaN, which
// would make the sort comparator non-deterministic).
const getTimestamp = (val: string | number | Date | null | undefined): number => {
	if (!val) return 0;
	if (typeof val === 'number') return val;
	const time = new Date(val).getTime();
	return isNaN(time) ? 0 : time;
};

/**
 * Deterministic chronological comparator for replies/questions. Orders by submission time
 * (created_at) ascending, with the monotonic `id` as a stable tiebreaker so equal or missing
 * timestamps never produce a non-deterministic order. Typed as the Map's value union so the
 * `Array.from(map)` entries pass directly (both members expose created_at/id for reads).
 */
export const byCreatedAtThenId = (
	a: ISocketCommentProps | IQuestionSocket,
	b: ISocketCommentProps | IQuestionSocket,
): number => {
	const aTime = getTimestamp(a?.created_at);
	const bTime = getTimestamp(b?.created_at);
	if (aTime !== bTime) return aTime - bTime;
	return (Number(a?.id) || 0) - (Number(b?.id) || 0);
};

/**
 * Sort a flat questions+comments Map (as [key, value] entries) into a stable, chronological
 * hierarchy: questions ordered by (created_at, id), with each question's replies ordered by
 * (created_at, id) directly beneath it. Question vs comment is determined by the Map key prefix
 * ("question_comment-" vs "question-") set by the moderator reducer — not raw id, which can collide
 * across the two separate serial sequences. Orphan comments (whose parent question is not present)
 * are appended at the end, grouped and sorted, so they are never dropped.
 */
export const sortQuestionsAndComments = (entries: QuestionsMapEntry[]): QuestionsMapEntry[] => {
	const questions: QuestionsMapEntry[] = [];
	const commentsByQuestion = new Map<number, QuestionsMapEntry[]>();

	for (const entry of entries) {
		if (String(entry?.[0]).startsWith('question_comment-')) {
			const questionId = Number((entry?.[1] as ISocketCommentProps)?.question_id);
			if (isNaN(questionId)) continue; // unlikely, but if malformed comment missing question_id, skip it
			const group = commentsByQuestion.get(questionId);
			if (group) {
				group.push(entry);
			} else {
				commentsByQuestion.set(questionId, [entry]);
			}
		} else {
			questions.push(entry);
		}
	}

	questions.sort((a, b) => byCreatedAtThenId(a[1], b[1]));

	const result: QuestionsMapEntry[] = [];
	const placedQuestionIds = new Set<number>();
	for (const question of questions) {
		const questionId = Number(question?.[1]?.id);
		placedQuestionIds.add(questionId);
		result.push(question);
		const comments = commentsByQuestion.get(questionId);
		if (comments) {
			comments.sort((a, b) => byCreatedAtThenId(a[1], b[1]));
			result.push(...comments);
		}
	}

	// never drop replies whose parent question isn't in the current list
	for (const [questionId, comments] of commentsByQuestion) {
		if (!placedQuestionIds.has(questionId)) {
			comments.sort((a, b) => byCreatedAtThenId(a[1], b[1]));
			result.push(...comments);
		}
	}

	return result;
};